/**
 * permissionControls — RBAC-2.1 · FR-9c. The ONE mapping from a role-editor control position to
 * the permission strings it grants, derived from an audit of every call site rather than asserted.
 *
 * ⚠️ WHY THIS EXISTS. Three naming generations overlap and the code reads them two ways.
 * `PermissionGuard.tsx:149` ORs `view_projects`, `project.view_all` and `project.view_team`, so any
 * one opens the gate and the dotted suffix means nothing there. `RoleDebugger.tsx:23` reads
 * `project.view_team && !project.view_all` as a real scope test. One pair of controls cannot
 * produce both readings, so this module states which one is true, per noun, and the test beside it
 * fails when a call site appears that the statement does not cover.
 *
 * The audit, with every live query behind it:
 * `_bmad-output/implementation-artifacts/rbac-2-1-call-site-audit-2026-11-01.md`.
 *
 * The model, per noun:
 *  - **Capability** is an ordered ladder (View < Edit < Manage). A rung grants every rung below it.
 *  - **Scope** says whose records: Own, Their team, Everyone. Everyone implies Their team. It is a
 *    second control only where the database narrows records by it (`enforced`), or will once its
 *    reader lands (`reader_pending`). Where scope strings exist but nothing narrows by them
 *    (`dormant`), there is NO scope control (FR-9a, NFR-13): a switch that changes nothing lies.
 *  - A **fused** string carries both axes in one name (`project.view_team`). It is the older
 *    generation; it is read as its capability rung, and its suffix is honoured only where the
 *    noun's scope is enforced.
 *
 * Field and Lead nouns are NOT restated here: they are read off `fieldPermissions.ts`, which the
 * KNK-0.5 migration test already pins. Only the legacy nouns are declared in this file.
 *
 * Pure, no DOM, no Supabase.
 */
import { CAPABILITY_LADDERS, FIELD_PERMISSIONS, SCOPE_LADDERS, type Scope } from './fieldPermissions';
import { ADDED_SINCE_RBAC_0_3, HIDDEN_PERMISSIONS } from './permissionDisplay';

export type ScopeStatus = 'enforced' | 'reader_pending' | 'dormant';

export interface CapabilityRung {
  rung: string;
  /** Every string that means this rung. The first is canonical. */
  strings: readonly string[];
}

export interface NounScope {
  status: ScopeStatus;
  /** `own`: holding the capability alone reaches the holder's own records. `none`: it reaches nothing. */
  floor: 'own' | 'none';
  team: readonly string[];
  all: readonly string[];
  /** The live reader that narrows by it, or why nothing does. */
  evidence: string;
}

export interface NounControls {
  noun: string;
  capability: readonly CapabilityRung[];
  scope: NounScope | null;
  /** AC1: what a flat (undotted, unscoped) string means when no dotted string is present. */
  flatMeaning: string;
}

export type StringReading =
  | { kind: 'capability'; noun: string; rung: string }
  | { kind: 'scope'; noun: string; scope: 'team' | 'all' }
  | { kind: 'fused'; noun: string; rung: string; scope: 'team' | 'all' }
  | { kind: 'switch' | 'destructive' | 'reserved'; note?: string }
  | { kind: 'hidden'; note: string }
  | { kind: 'phantom'; note: string };

// ─────────────────────────────────────────────────────────────────────────────
// Nouns
// ─────────────────────────────────────────────────────────────────────────────

const OWN_FLAT = 'Its holder reaches their own records only; a scope string widens that.';
const NO_SCOPE = 'There is no scope string for this noun, so the flat string is the whole grant: every record in the account the screens show.';

const LEGACY: NounControls[] = [
  {
    noun: 'project',
    capability: [
      { rung: 'view', strings: ['view_projects', 'project.view_team', 'project.view_all'] },
      // project.manage_team is on EDIT, not Manage: its only two readers (ProjectEditGuard and
      // ProjectOverviewTab) also accept edit_projects, and every Manage gate reads
      // manage_projects || project.manage_all without it. Pinned by the test.
      { rung: 'edit', strings: ['edit_projects', 'project.manage_team'] },
      { rung: 'manage', strings: ['manage_projects', 'project.manage_all'] },
    ],
    scope: {
      status: 'dormant',
      floor: 'own',
      team: ['project.view_team', 'project.manage_team'],
      all: ['project.view_all', 'project.manage_all'],
      evidence:
        'No policy or function names any project.* string. projects_select decides rows by role NAME (user_has_role). '
        + 'Every client site ORs the suffixes with each other or with the flat string; the only site that tells them apart is RoleDebugger, which prints a label.',
    },
    flatMeaning:
      'view_projects / edit_projects / manage_projects are the rung, with no scope stated. Which projects are seen is decided by the role name in projects_select, not by any string here.',
  },
  {
    noun: 'user',
    capability: [{ rung: 'manage', strings: ['manage_users', 'user.manage_team'] }],
    scope: {
      status: 'dormant',
      floor: 'own',
      team: ['user.manage_team'],
      all: [],
      evidence:
        'user.manage_team is read by no policy or function; every reader (UserManageGuard, create-admin-user, require_caller) ORs it with manage_users. '
        + 'user.manage_all is NOT its Everyone rung: it is read by users_update and users_delete RLS, so it is a separate destructive switch.',
    },
    flatMeaning: 'manage_users opens the user-management screens and the user-creation Edge Functions. It does not let its holder write another user row directly.',
  },
  {
    noun: 'pipeline',
    capability: [
      { rung: 'edit', strings: ['edit_pipeline'] },
      { rung: 'manage', strings: ['pipeline.manage_all'] },
    ],
    scope: null,
    flatMeaning: `${NO_SCOPE} The _all in pipeline.manage_all is not a scope: no team form exists.`,
  },
  {
    noun: 'finance',
    capability: [
      { rung: 'view', strings: ['view_finance'] },
      { rung: 'edit', strings: ['edit_finance'] },
    ],
    scope: null,
    flatMeaning: NO_SCOPE,
  },
  {
    noun: 'contracts',
    capability: [
      { rung: 'view', strings: ['view_contracts'] },
      { rung: 'manage', strings: ['manage_contracts'] },
    ],
    scope: null,
    flatMeaning: NO_SCOPE,
  },
  {
    // view_tickets and edit_tickets gate nothing (RBAC-2.2, TRIAGE_CORRECTIONS): one rung.
    noun: 'tickets',
    capability: [{ rung: 'manage', strings: ['manage_tickets'] }],
    scope: null,
    flatMeaning: NO_SCOPE,
  },
  {
    noun: 'teams',
    capability: [
      { rung: 'edit', strings: ['edit_teams'] },
      { rung: 'manage', strings: ['manage_teams'] },
    ],
    scope: null,
    flatMeaning: NO_SCOPE,
  },
];

/** Rung names for the field ladders whose base is a verb rather than a noun. */
const FIELD_RUNG: Record<string, string> = {
  'knock.review': 'review',
  'field.live_view': 'view',
  'field.playback': 'replay',
  'report.field': 'view',
  'report.statement': 'view',
};

const FIELD_SCOPE_EVIDENCE: Record<string, { status: ScopeStatus; evidence: string }> = {
  'lead.view': { status: 'enforced', evidence: 'pin_in_reach(): the capability, then lead.scope.all, else lead.scope.team over team_reach().' },
  'door.view': { status: 'enforced', evidence: 'door_map_in_bbox() / door_units() / door_sheet(): door.scope.all, else door.scope.team crew, else own.' },
  'territory.view': { status: 'enforced', evidence: 'area_reach(): territory.scope.all, else own or shared, else territory.scope.team crew.' },
  'knock.review': { status: 'enforced', evidence: 'knock_in_reach() / knock_review_gate(): knock.review AND (.all OR .team over team_reach()).' },
  'field.live_view': { status: 'enforced', evidence: 'live_team_map() / location_in_reach(): .team or .all. The flat field.live_view has no database reader.' },
  'field.playback': { status: 'reader_pending', evidence: 'No reader yet; declared in fieldPermissions.ts ahead of its story.' },
  'report.field': { status: 'reader_pending', evidence: 'No reader yet; declared in fieldPermissions.ts ahead of its story.' },
  'report.statement': { status: 'reader_pending', evidence: 'No reader yet; declared in fieldPermissions.ts ahead of its story.' },
};

function fieldNouns(): NounControls[] {
  const out: NounControls[] = [];
  for (const [noun, rungs] of Object.entries(CAPABILITY_LADDERS)) {
    const ladder = SCOPE_LADDERS.find((l) => rungs.includes(l.base));
    const ev = ladder ? FIELD_SCOPE_EVIDENCE[ladder.base] : undefined;
    out.push({
      noun,
      capability: rungs.map((s) => ({ rung: s.slice(noun.length + 1), strings: [s] })),
      scope: ladder && ev ? { ...ev, floor: ladder.floor, team: [ladder.team], all: [ladder.all] } : null,
      flatMeaning: OWN_FLAT,
    });
  }
  for (const l of SCOPE_LADDERS) {
    if (Object.values(CAPABILITY_LADDERS).some((r) => r.includes(l.base))) continue;
    const ev = FIELD_SCOPE_EVIDENCE[l.base];
    const noun = l.base.split('.')[0];
    out.push({
      // `door.view` is the only rung of `door`; a verb ladder (`knock.review`) is its own noun.
      noun: noun === 'door' ? 'door' : l.base,
      capability: [{ rung: FIELD_RUNG[l.base] ?? l.base.slice(noun.length + 1), strings: [l.base] }],
      scope: { ...ev, floor: l.floor, team: [l.team], all: [l.all] },
      flatMeaning: l.floor === 'own'
        ? OWN_FLAT
        : 'The flat string alone reaches nobody else\'s records. It must be held with a scope string.',
    });
  }
  return out;
}

export const NOUNS: Readonly<Record<string, NounControls>> = Object.fromEntries(
  [...LEGACY, ...fieldNouns()].map((n) => [n.noun, n]),
);

// ─────────────────────────────────────────────────────────────────────────────
// Every string, read
// ─────────────────────────────────────────────────────────────────────────────

// Hidden rows are RBAC-2.2's `HIDDEN_PERMISSIONS`, not restated: one list, one reason per row.

const RESERVED = ['system.override', 'manage_settings', 'settings.admin', 'tenant.manage'];

const LEGACY_SWITCHES = [
  'commission.view', 'commission.approve', 'commission.adjust', 'commission.pay', 'comp.agreement.manage',
  'cost.actual.record', 'cost.category.manage', 'cost.rate_card.manage', 'cost.standard_rate.accept', 'cost.view',
  'project.send_email', 'reporting.admin', 'manage_roles', 'manage_checklists',
  'view_residential_pipeline', 'view_commercial_pipeline', 'view_onboarding_pipeline',
];

const PHANTOM_NOTE = 'Not in the catalogue. hasPermission() is a set lookup, so every check of it is false; the gate works through its other arms.';
const PHANTOMS = [
  'view_partners', 'edit_partners', 'manage_partners', 'view_work_orders', 'edit_work_orders',
  'manage_work_orders', 'view_sales_team', 'manage_sales_team', 'manage_finance',
];

function buildReadings(): Record<string, StringReading> {
  const r: Record<string, StringReading> = {};
  for (const n of Object.values(NOUNS)) {
    for (const c of n.capability) {
      for (const s of c.strings) {
        const team = n.scope?.team.includes(s);
        const all = n.scope?.all.includes(s);
        r[s] = team || all
          ? { kind: 'fused', noun: n.noun, rung: c.rung, scope: all ? 'all' : 'team' }
          : { kind: 'capability', noun: n.noun, rung: c.rung };
      }
    }
    for (const s of n.scope?.team ?? []) r[s] ??= { kind: 'scope', noun: n.noun, scope: 'team' };
    for (const s of n.scope?.all ?? []) r[s] ??= { kind: 'scope', noun: n.noun, scope: 'all' };
  }
  for (const f of FIELD_PERMISSIONS) {
    if (r[f.key]) continue;
    r[f.key] = { kind: f.kind === 'destructive' ? 'destructive' : 'switch' };
  }
  for (const s of LEGACY_SWITCHES) r[s] = { kind: 'switch' };
  r['user.manage_all'] = {
    kind: 'destructive',
    note: 'Read by users_update and users_delete RLS (and the videos/playlists policies). Not the Everyone rung of user.manage_team.',
  };
  for (const s of Object.keys(ADDED_SINCE_RBAC_0_3)) {
    r[s] ??= { kind: 'switch', note: `Added after RBAC-0.3 by ${ADDED_SINCE_RBAC_0_3[s]}.` };
  }
  for (const [s, note] of Object.entries(HIDDEN_PERMISSIONS)) r[s] = { kind: 'hidden', note };
  for (const s of RESERVED) r[s] = { kind: 'reserved', note: 'FR-12: locked. permissions.is_reserved is the definition.' };
  for (const s of PHANTOMS) r[s] = { kind: 'phantom', note: PHANTOM_NOTE };
  return r;
}

export const PERMISSION_READINGS: Readonly<Record<string, StringReading>> = buildReadings();

// ─────────────────────────────────────────────────────────────────────────────
// Control position <-> strings
// ─────────────────────────────────────────────────────────────────────────────

function noun(name: string): NounControls {
  const n = NOUNS[name];
  if (!n) throw new Error(`permissionControls: unknown noun "${name}"`);
  return n;
}

const hasScopeControl = (n: NounControls) => !!n.scope && n.scope.status !== 'dormant';

/**
 * The strings a role holds at one control position. Sorted, de-duplicated.
 *
 * A dormant scope writes every string of each rung, fused ones included, because the legacy gates
 * disagree about which of them they read (`Redlines.tsx` reads only the dotted pair) and none of
 * them narrows by the suffix.
 */
export function grantsFor(nounName: string, capability: string | null, scope: Scope | null): string[] {
  const n = noun(nounName);
  if (capability === null) return [];
  const top = n.capability.findIndex((c) => c.rung === capability);
  if (top < 0) throw new Error(`permissionControls: ${nounName} has no rung "${capability}"`);
  const out = new Set(n.capability.slice(0, top + 1).flatMap((c) => c.strings));

  if (!hasScopeControl(n)) {
    if (scope !== null) throw new Error(`permissionControls: ${nounName} has no scope control`);
  } else {
    if (scope === null) throw new Error(`permissionControls: ${nounName} needs a scope`);
    if (scope === 'own' && n.scope!.floor === 'none') {
      throw new Error(`permissionControls: ${nounName} has no Own rung; its capability reaches nothing without a scope`);
    }
    if (scope === 'team' || scope === 'all') n.scope!.team.forEach((s) => out.add(s));
    if (scope === 'all') n.scope!.all.forEach((s) => out.add(s));
  }
  return [...out].sort();
}

export interface ControlPosition {
  capability: string | null;
  scope: Scope | null;
  /**
   * What saving this position unchanged would ADD to the role. Non-empty when a role holds part of
   * a rung, which the legacy gates read inconsistently: live, Team Owner holds the dotted View
   * strings but not `view_projects`, so it fails `App.tsx`'s commercial-pipeline gate today. The
   * RBAC-3.6 diff must show these as gains rather than save them silently.
   */
  saveAdds: string[];
}

/** Where an existing role's strings put the two controls. The highest rung held wins. */
export function readControls(nounName: string, held: readonly string[]): ControlPosition {
  const n = noun(nounName);
  const h = new Set(held);
  let top = -1;
  n.capability.forEach((c, i) => {
    if (c.strings.some((s) => h.has(s))) top = i;
  });
  const capability = top >= 0 ? n.capability[top].rung : null;

  let scope: Scope | null = null;
  if (hasScopeControl(n)) {
    const sc = n.scope!;
    if (sc.all.some((s) => h.has(s))) scope = 'all';
    else if (sc.team.some((s) => h.has(s))) scope = 'team';
    else if (sc.floor === 'own' && capability !== null) scope = 'own';
  }
  const legal = capability !== null
    && !(hasScopeControl(n) && (scope === null || (scope === 'own' && n.scope!.floor === 'none')));
  const saveAdds = legal ? grantsFor(nounName, capability, scope).filter((s) => !h.has(s)) : [];
  return { capability, scope, saveAdds };
}

// ─────────────────────────────────────────────────────────────────────────────
// Call sites
// ─────────────────────────────────────────────────────────────────────────────

export interface CallSite {
  path: string;
  line: number;
  /** Sorted, de-duplicated. */
  strings: string[];
}

const LITERAL = /['"`]([a-z][a-z_]*(?:\.[a-z_]+)*)['"`]/g;
const isComment = (line: string) => /^\s*(\/\/|\*|\/\*|\{\/\*)/.test(line);

/**
 * A call site is a run of consecutive non-comment lines each naming a string the mapping knows.
 * A multi-line `permissions={[...]}` array or an `a || b || c` chain is therefore one site.
 */
export function findCallSites(path: string, text: string): CallSite[] {
  const sites: CallSite[] = [];
  let start = 0;
  let acc = new Set<string>();
  const lines = text.split('\n');
  lines.push('');
  lines.forEach((l, i) => {
    const found = isComment(l)
      ? []
      : [...l.matchAll(LITERAL)].map((m) => m[1]).filter((s) => s in PERMISSION_READINGS);
    if (found.length) {
      if (acc.size === 0) start = i + 1;
      found.forEach((s) => acc.add(s));
    } else if (acc.size) {
      sites.push({ path, line: start, strings: [...acc].sort() });
      acc = new Set();
    }
  });
  return sites;
}

/** The nouns a set of strings reads on more than one axis position (flat, team, all). */
export function crossAxisNouns(strings: readonly string[]): string[] {
  const positions = new Map<string, Set<string>>();
  for (const s of strings) {
    const r = PERMISSION_READINGS[s];
    if (!r || !('noun' in r)) continue;
    const pos = r.kind === 'capability' ? 'flat' : r.scope;
    if (!positions.has(r.noun)) positions.set(r.noun, new Set());
    positions.get(r.noun)!.add(pos);
  }
  return [...positions].filter(([, p]) => p.size > 1).map(([n]) => n).sort();
}

const CHECK_CALL =
  /\b(?:hasPermission|hasAnyPermission|hasAllPermissions|useHasPermission|useHasAnyPermission|useHasAllPermissions|requirePermission|holdsFieldPermission|holdsCommissionPermission)\s*\(|\bpermissions?=\s*/g;

/** Strings passed to a permission check that the mapping does not know. `path:line string`. */
export function uncataloguedChecks(path: string, text: string): string[] {
  const out: string[] = [];
  const lines = text.split('\n');
  const lineAt = (offset: number) => text.slice(0, offset).split('\n').length;
  for (const m of text.matchAll(CHECK_CALL)) {
    const at = m.index ?? 0;
    if (isComment(lines[lineAt(at) - 1])) continue;
    const rest = text.slice(at + m[0].length);
    const arg = rest.match(/^\s*["'`][^"'`]*["'`]/)?.[0] ?? rest.slice(0, rest.search(/[)}]/) + 1 || 0);
    for (const lit of arg.matchAll(LITERAL)) {
      if (lit[1] in PERMISSION_READINGS) continue;
      out.push(`${path}:${lineAt(at + m[0].length + (lit.index ?? 0))} ${lit[1]}`);
    }
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// AC2 · how each cross-axis call site is to be read
// ─────────────────────────────────────────────────────────────────────────────

export type SiteReading =
  /** ORs one capability across scope positions: "holds the rung at any scope". Matches the mapping. */
  | 'any-scope'
  /** Tells scope rungs apart, or requires a capability AND a scope: reads the two controls together. */
  | 'scope-test'
  /** Decides nothing: printed in a debug card, or a hook field no file consumes. */
  | 'decides-nothing';

export interface RecordedReading {
  reading: SiteReading;
  note: string;
}

export const callSiteKey = (path: string, strings: readonly string[]) =>
  `${path} :: ${[...strings].sort().join(' ')}`;

const site = (path: string, strings: string[], reading: SiteReading, note: string): [string, RecordedReading] =>
  [callSiteKey(path, strings), { reading, note }];

const P = 'frontend/src/';
const ANY_VIEW = 'Any one of the View strings opens it: the View rung at any scope.';
const ANY_EDIT = 'Opens on edit_projects or above: the Edit rung, any scope.';
const ANY_MANAGE =
  'Reads manage_projects || project.manage_all and NOT project.manage_team: the Manage rung, any scope. The editor writes both, so a role saved at Manage passes.';
const INTERNAL =
  'An "is internal staff" OR across nouns. Opens on the Edit rung of projects or above; the phantom arms are always false.';
const SERVER_NARROWS =
  'Shows the screen on either scope rung; the server function narrows rows by .team vs .all. Matches the mapping.';
const BIG_OR = [
  'edit_projects', 'edit_work_orders', 'manage_contracts', 'manage_projects', 'manage_roles', 'manage_sales_team',
  'manage_users', 'manage_work_orders', 'project.manage_all', 'settings.admin', 'view_contracts', 'view_sales_team',
  'view_work_orders',
];
const EDIT_OVERRIDE = ['edit_projects', 'manage_projects', 'project.manage_all', 'system.override', 'tenant.manage'];

export const CALL_SITE_READINGS: Readonly<Record<string, RecordedReading>> = Object.fromEntries([
  // ── The two FR-9c names: neither is rendered ───────────────────────────────
  site(`${P}components/PermissionGuard.tsx`, ['project.view_all', 'project.view_team', 'view_projects'], 'decides-nothing',
    `ProjectViewGuard, the FR-9c site. No file renders it. Its reading (${ANY_VIEW}) is live inline at the two App.tsx project routes.`),
  site(`${P}components/debug/RoleDebugger.tsx`, ['manage_roles', 'manage_users', 'project.view_all', 'project.view_team', 'settings.admin'], 'decides-nothing',
    'The FR-9c site. isBizDevRep (team && !all) is printed in a debug card that Dashboard.tsx imports and never renders. It is the only team-vs-all reading of a project string anywhere.'),
  site(`${P}components/examples/UserRoleExample.tsx`, ['manage_roles', 'manage_users', 'project.view_all', 'project.view_team', 'settings.admin'], 'decides-nothing',
    "A copy of RoleDebugger's isBizDevRep in an example component nothing renders."),
  site(`${P}hooks/useOptimizedPermissions.ts`, ['delete_projects', 'edit_projects', 'manage_projects', 'project.view_all', 'project.view_team'], 'decides-nothing',
    'canViewAllProjects / canViewTeamProjects and the isBizDevRep / isSalesRep derived from them are read by no file; the hook\'s three consumers take isSystemAdmin, isSuperadmin and canManageSettings.'),

  // ── Projects: View ─────────────────────────────────────────────────────────
  site(`${P}App.tsx`, ['project.view_all', 'project.view_team', 'view_projects'], 'any-scope', `Two project routes. ${ANY_VIEW}`),
  site(`${P}App.tsx`, ['project.view_all', 'view_commercial_pipeline', 'view_projects'], 'any-scope',
    'Commercial pipeline route. Omits project.view_team, so Team Owner (dotted View strings, no view_projects) fails it today; saving it at View adds view_projects.'),
  site(`${P}pages/PipelineResi.tsx`, ['manage_projects', 'project.view_all', 'view_projects'], 'any-scope',
    'canViewFinance: View or Manage. Omits project.view_team; the same Team Owner gap as the commercial route.'),
  site(`${P}pages/FAQ.tsx`, ['project.view_all', 'project.view_team', 'view_projects'], 'any-scope', `State pricing. ${ANY_VIEW}`),
  site(`${P}components/RedlinesMap.tsx`, ['project.view_all', 'project.view_team'], 'any-scope',
    'Reads only the dotted pair, as one rung. The reason a dormant scope writes every string of the rung.'),
  site(`${P}pages/Redlines.tsx`, ['project.view_all', 'project.view_team'], 'any-scope', 'As RedlinesMap.'),
  site(`${P}pages/SESRedlines.tsx`, ['project.view_all', 'project.view_team'], 'any-scope', 'As RedlinesMap.'),
  site(`${P}pages/CommercialRedlines.tsx`, ['project.view_all', 'project.view_team'], 'any-scope', 'As RedlinesMap.'),

  // ── Projects: Edit ─────────────────────────────────────────────────────────
  site(`${P}components/PermissionGuard.tsx`, ['edit_projects', 'manage_projects', 'project.manage_all', 'project.manage_team'], 'decides-nothing',
    `ProjectEditGuard. ProjectOverviewTab imports it and never renders it. Would read: ${ANY_EDIT}`),
  site(`${P}components/project/tabs/ProjectOverviewTab.tsx`, ['edit_projects', 'manage_projects', 'project.manage_all', 'project.manage_team'], 'any-scope',
    `canEditProject. ${ANY_EDIT} The only live reader of project.manage_team.`),
  site(`${P}components/modals/ProjectModal.tsx`, EDIT_OVERRIDE, 'any-scope', `canEditProject. ${ANY_EDIT}`),
  site(`${P}components/project/ProjectOverviewHeader.tsx`, EDIT_OVERRIDE, 'any-scope', `canEditProject. ${ANY_EDIT}`),
  site(`${P}components/project/ProjectModalTabRenderer.tsx`, EDIT_OVERRIDE, 'any-scope', `hasManagementPermission, despite the name. ${ANY_EDIT}`),
  site(`${P}components/project/ProjectModalSidebar.tsx`, ['edit_projects', 'manage_projects', 'project.manage_all'], 'any-scope', `hasProjectManagement, despite the name. ${ANY_EDIT}`),
  site(`${P}components/chat/ProjectChat.tsx`, ['edit_projects', 'manage_projects', 'project.manage_all'], 'any-scope', `isSalesStaff. ${ANY_EDIT}`),

  // ── Projects: Manage ───────────────────────────────────────────────────────
  site(`${P}components/FinanciersTab.tsx`, ['manage_projects', 'manage_settings', 'project.manage_all'], 'any-scope', `canManageFinance. ${ANY_MANAGE}`),
  site(`${P}components/chat/ProjectChat.tsx`, ['manage_projects', 'project.manage_all'], 'any-scope', `canManageChat. ${ANY_MANAGE}`),
  site(`${P}lib/stageMoveGate.ts`, ['manage_projects', 'project.manage_all', 'system.override'], 'any-scope', `Direct stage moves. ${ANY_MANAGE}`),

  // ── "Internal staff" ORs across nouns ──────────────────────────────────────
  site(`${P}components/RedlinesProtectedRoute.tsx`, ['edit_finance', 'edit_projects', 'edit_work_orders', 'manage_contracts', 'manage_finance', 'manage_projects', 'manage_roles', 'manage_sales_team', 'manage_users', 'manage_work_orders', 'project.manage_all', 'settings.admin', 'view_contracts', 'view_finance', 'view_projects', 'view_sales_team', 'view_work_orders'], 'any-scope',
    'Redlines page access. Opens on view_projects among many arms; the dotted View strings are not in it, so Team Owner reaches it only through edit_projects.'),
  site(`${P}components/chat/OrganizationChat.tsx`, BIG_OR, 'any-scope', INTERNAL),
  site(`${P}components/chat/ProjectChat.tsx`, BIG_OR, 'any-scope', INTERNAL),
  site(`${P}pages/NewTicket.tsx`, BIG_OR.filter((s) => s !== 'edit_projects'), 'any-scope',
    'isInternalRole. As the chat ORs without edit_projects: the Manage rung of projects, or the other arms.'),
  site(`${P}components/modals/OnboardingOrganizationModal.tsx`, ['edit_projects', 'manage_projects', 'manage_roles', 'manage_users', 'project.manage_all', 'settings.admin'], 'any-scope', `isManagerOrAdmin. ${ANY_EDIT}`),
  site(`${P}components/modals/ProjectModal.tsx`, ['edit_projects', 'manage_projects', 'manage_roles', 'manage_users', 'project.manage_all', 'settings.admin', 'system.override', 'tenant.manage'], 'any-scope', `hasManagementPermissions. ${ANY_EDIT}`),
  site(`${P}components/project/tabs/ProjectNotesTab.tsx`, ['edit_projects', 'manage_projects', 'manage_roles', 'manage_users', 'project.manage_all', 'settings.admin', 'system.override', 'tenant.manage', 'view_projects'], 'any-scope',
    'hasInternalRolePermissions. Opens on view_projects but not the dotted View strings: Team Owner reaches it through edit_projects.'),

  // ── People ─────────────────────────────────────────────────────────────────
  site(`${P}components/PermissionGuard.tsx`, ['manage_users', 'user.manage_all', 'user.manage_team'], 'any-scope',
    'UserManageGuard. Any of the three opens the screens. user.manage_all also carries row writes and deletes through RLS, which is why it is a separate switch.'),
  site('supabase/functions/create-admin-user/index.ts', ['manage_users', 'user.manage_all', 'user.manage_team'], 'any-scope',
    'requirePermission, as UserManageGuard. What may be assigned is bounded by requireRoleAssignable and requireReservedHeld, not by scope.'),

  // ── Field: the server narrows ──────────────────────────────────────────────
  site(`${P}lib/knockReview.ts`, ['knock.review.all', 'knock.review.team', 'system.override'], 'any-scope', `KNOCK_REVIEW_PERMISSIONS. ${SERVER_NARROWS}`),
  site(`${P}lib/liveTeamMap.ts`, ['field.live_view.all', 'field.live_view.team', 'system.override'], 'any-scope', `LIVE_MAP_PERMISSIONS. ${SERVER_NARROWS}`),
]);
